export const BOOKING_COLORS = [
	'red',
	'orange',
	'brown',
	'yellow',
	'purple',
	'pink',
	'blue',
	'black',
	'green'
] as const;

export type BookingColor = (typeof BOOKING_COLORS)[number];

export const DEFAULT_BOOKING_COLOR: BookingColor = 'green';

const palettes: Record<BookingColor, Record<string, string>> = {
	red: {
		'50': '254 242 242',
		'100': '254 226 226',
		'200': '254 202 202',
		'300': '252 165 165',
		'400': '248 113 113',
		'500': '239 68 68',
		'600': '220 38 38',
		'700': '185 28 28',
		'800': '153 27 27',
		'900': '127 29 29',
		'950': '69 10 10'
	},
	orange: {
		'50': '255 247 237',
		'100': '255 237 213',
		'200': '254 215 170',
		'300': '253 186 116',
		'400': '251 146 60',
		'500': '249 115 22',
		'600': '234 88 12',
		'700': '194 65 12',
		'800': '154 52 18',
		'900': '124 45 18',
		'950': '67 20 7'
	},
	brown: {
		'50': '251 247 244',
		'100': '243 233 225',
		'200': '230 208 192',
		'300': '212 173 142',
		'400': '189 138 94',
		'500': '161 110 63',
		'600': '133 90 50',
		'700': '107 72 40',
		'800': '84 57 33',
		'900': '67 48 29',
		'950': '39 27 16'
	},
	yellow: {
		'50': '254 252 232',
		'100': '254 249 195',
		'200': '254 240 138',
		'300': '253 224 71',
		'400': '250 204 21',
		'500': '234 179 8',
		'600': '202 138 4',
		'700': '161 98 7',
		'800': '133 77 14',
		'900': '113 63 18',
		'950': '66 32 6'
	},
	purple: {
		'50': '250 245 255',
		'100': '243 232 255',
		'200': '233 213 255',
		'300': '216 180 254',
		'400': '192 132 252',
		'500': '168 85 247',
		'600': '147 51 234',
		'700': '126 34 206',
		'800': '107 33 168',
		'900': '88 28 135',
		'950': '59 7 100'
	},
	pink: {
		'50': '253 242 248',
		'100': '252 231 243',
		'200': '251 207 232',
		'300': '249 168 212',
		'400': '244 114 182',
		'500': '236 72 153',
		'600': '219 39 119',
		'700': '190 24 93',
		'800': '157 23 77',
		'900': '131 24 67',
		'950': '80 7 36'
	},
	blue: {
		'50': '239 246 255',
		'100': '219 234 254',
		'200': '191 219 254',
		'300': '147 197 253',
		'400': '96 165 250',
		'500': '59 130 246',
		'600': '37 99 235',
		'700': '29 78 216',
		'800': '30 64 175',
		'900': '30 58 138',
		'950': '23 37 84'
	},
	black: {
		'50': '247 247 247',
		'100': '237 237 237',
		'200': '209 209 209',
		'300': '176 176 176',
		'400': '112 112 112',
		'500': '51 51 51',
		'600': '38 38 38',
		'700': '26 26 26',
		'800': '17 17 17',
		'900': '10 10 10',
		'950': '0 0 0'
	},
	green: {
		'50': '236 253 245',
		'100': '209 250 229',
		'200': '167 243 208',
		'300': '110 231 183',
		'400': '52 211 153',
		'500': '16 185 129',
		'600': '5 150 105',
		'700': '4 120 87',
		'800': '6 95 70',
		'900': '4 78 56',
		'950': '2 44 34'
	}
};

export const isBookingColor = (value: unknown): value is BookingColor =>
	typeof value === 'string' &&
	(BOOKING_COLORS as readonly string[]).includes(value);

// Inline `--primary-*` overrides to bind on the booking page root via :style.
export const bookingThemeVars = (color: BookingColor): Record<string, string> =>
	Object.fromEntries(
		Object.entries(palettes[color] ?? palettes[DEFAULT_BOOKING_COLOR]).map(
			([shade, rgb]) => [`--primary-${shade}`, rgb]
		)
	);
